import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { readSheet } from 'read-excel-file/browser';

import { PurchaseOrderService, PoBulkResult, BulkImportItem } from '#services/api/purchase-order.service';
import { DictionaryService } from '#services/api';
import { ToasterService } from '#services/shared';
import { Dictionary } from 'models';
import { Dictionaries } from 'enums/dictionary';

interface BulkRow {
    PurchaseOrderNumber: string;
    RequestNumber: string;
    SubmitDate: string;
    FabricatorID: number;
    _vendorName: string;
    _errors: string[];
}

@Component({
    selector: 'app-po-bulk-import-dialog',
    templateUrl: './po-bulk-import-dialog.component.html',
    styleUrls: ['./po-bulk-import-dialog.component.scss'],
    standalone: false
})
export class PoBulkImportDialogComponent implements OnInit {

    state: 'idle' | 'previewing' | 'submitting' | 'done' = 'idle';

    file: File | null = null;
    parseError: string | null = null;
    rows: BulkRow[] = [];
    fabricators: Dictionary[] = [];
    result: PoBulkResult | null = null;

    readonly previewColumns = ['row', 'PONumber', 'RequestNumber', 'SubmitDate', 'Vendor', 'Errors'];
    readonly errorColumns   = ['row', 'identifier', 'message'];

    constructor(
        public dialogRef: MatDialogRef<PoBulkImportDialogComponent>,
        private poService: PurchaseOrderService,
        private dictionaryService: DictionaryService,
        private toaster: ToasterService
    ) {}

    ngOnInit() {
        this.dictionaryService
            .getAll(`${Dictionaries.fabricators}/dictionary`)
            .toPromise()
            .then(list => { this.fabricators = list || []; });
    }

    get hasRowErrors(): boolean {
        return this.rows.some(r => r._errors.length > 0);
    }

    get validRowCount(): number {
        return this.rows.filter(r => r._errors.length === 0).length;
    }

    onFileInputChange(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            this.processFile(input.files[0]);
        }
    }

    onFileDrop(event: DragEvent) {
        event.preventDefault();
        if (event.dataTransfer && event.dataTransfer.files.length) {
            this.processFile(event.dataTransfer.files[0]);
        }
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
    }

    processFile(file: File) {
        this.file = file;
        this.parseError = null;

        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (ext === 'xlsx' || ext === 'xls') {
            this.parseXlsx(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => { if (e.target) { this.parseCSV(e.target.result as string); } };
        reader.onerror = () => {
            this.parseError = 'Could not read the file.';
            this.state = 'idle';
        };
        reader.readAsText(file);
    }

    parseCSV(text: string) {
        try {
            const lines = text.split(/\r?\n/).filter(l => l.trim());
            if (lines.length < 2) {
                this.parseError = 'File must have a header row and at least one data row.';
                this.state = 'idle';
                return;
            }

            const headers = lines[0].split(',');
            const dataRows = lines.slice(1).map(line => this.splitCSVLine(line));
            this.buildRows(headers, dataRows);
        } catch (e) {
            this.parseError = 'Failed to parse CSV. Please check the file format.';
            this.state = 'idle';
        }
    }

    parseXlsx(file: File) {
        readSheet(file)
            .then((rows) => {
                if (!rows || rows.length < 2) {
                    this.parseError = 'File must have a header row and at least one data row.';
                    this.state = 'idle';
                    return;
                }

                const headers = (rows[0] || []).map(c => this.cellToString(c));
                const dataRows = rows.slice(1).map(row => (row || []).map(c => this.cellToString(c)));
                this.buildRows(headers, dataRows);
            })
            .catch(() => {
                this.parseError = 'Failed to parse the Excel file. Please check the file format.';
                this.state = 'idle';
            });
    }

    /** Normalises a parsed Excel cell value to a trimmed string (dates become yyyy-MM-dd). */
    private cellToString(cell: unknown): string {
        if (cell == null) { return ''; }
        if (cell instanceof Date) {
            const y = cell.getFullYear();
            const m = String(cell.getMonth() + 1).padStart(2, '0');
            const d = String(cell.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        }
        return String(cell);
    }

    /**
     * Builds preview rows (with per-row validation) from a header row and data rows.
     * Shared by the CSV and Excel parsers so both show the same preview table.
     */
    private buildRows(rawHeaders: string[], dataRows: string[][]) {
        const headers = rawHeaders.map(h => (h || '').trim().replace(/"/g, '').toLowerCase());

        const col = (aliases: string[]) => {
            for (const alias of aliases) {
                const i = headers.indexOf(alias);
                if (i >= 0) { return i; }
            }
            return -1;
        };

        const idxPo   = col(['purchaseordernumber', 'ponumber', 'po number', 'po#', 'po']);
        const idxReq  = col(['requestnumber', 'request number', 'req number', 'request#', 'req#', 'req']);
        const idxDate = col(['submitdate', 'submit date', 'date']);
        const idxFabId   = col(['fabricatorid', 'vendorid', 'vendor id', 'fab id']);
        const idxFabName = col(['fabricator', 'vendor', 'vendor name', 'fabricator name']);

        this.rows = dataRows
            .filter(cols => cols.some(c => (c || '').trim() !== ''))
            .map((cols) => {
                const errors: string[] = [];

                const poNum      = idxPo   >= 0 ? (cols[idxPo]   || '').trim() : '';
                const reqNum     = idxReq  >= 0 ? (cols[idxReq]  || '').trim() : '';
                const submitDate = idxDate >= 0 ? (cols[idxDate] || '').trim() : '';

                let fabricatorId: number | null = null;
                let vendorName = '';

                if (idxFabId >= 0 && cols[idxFabId] && cols[idxFabId].trim()) {
                    const parsed = parseInt(cols[idxFabId].trim(), 10);
                    if (isNaN(parsed)) {
                        errors.push('FabricatorID must be a number.');
                    } else {
                        fabricatorId = parsed;
                        const found = this.fabricators.find(f => f.ID === parsed);
                        vendorName = found ? found.Name : `ID ${parsed} (unknown)`;
                        if (!found) {
                            errors.push(`Vendor ID ${parsed} was not found.`);
                        }
                    }
                } else if (idxFabName >= 0 && cols[idxFabName] && cols[idxFabName].trim()) {
                    const name = cols[idxFabName].trim();
                    const found = this.fabricators.find(
                        f => f.Name.toLowerCase() === name.toLowerCase()
                    );
                    if (found) {
                        fabricatorId = found.ID;
                        vendorName = found.Name;
                    } else {
                        vendorName = name;
                        errors.push(`Unknown vendor: "${name}".`);
                    }
                } else {
                    errors.push('Vendor is required (FabricatorID or Vendor column).');
                }

                if (!poNum && !reqNum) {
                    errors.push('PO Number or Request Number is required.');
                }

                if (poNum && !submitDate) {
                    errors.push('Submit Date is required when PO Number is provided.');
                }

                return {
                    PurchaseOrderNumber: poNum || null,
                    RequestNumber: reqNum || null,
                    SubmitDate: submitDate ? this.normaliseDate(submitDate) : null,
                    FabricatorID: fabricatorId,
                    _vendorName: vendorName,
                    _errors: errors
                } as BulkRow;
            });

        this.state = 'previewing';
    }

    /** Splits one CSV line respecting double-quoted fields. */
    private splitCSVLine(line: string): string[] {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                inQuotes = !inQuotes;
            } else if (ch === ',' && !inQuotes) {
                result.push(current);
                current = '';
            } else {
                current += ch;
            }
        }
        result.push(current);
        return result;
    }

    /**
     * Convert common date formats (M/D/YYYY, MM/DD/YYYY) to yyyy-MM-dd
     * which the backend DateFormatConverter expects.
     * Strings already in yyyy-MM-dd pass through unchanged.
     */
    private normaliseDate(raw: string): string {
        // Already ISO-ish
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            return raw;
        }
        // M/D/YYYY or MM/DD/YYYY
        const parts = raw.split('/');
        if (parts.length === 3) {
            const m = parts[0].padStart(2, '0');
            const d = parts[1].padStart(2, '0');
            const y = parts[2];
            return `${y}-${m}-${d}`;
        }
        return raw;
    }

    reset() {
        this.state = 'idle';
        this.file = null;
        this.rows = [];
        this.parseError = null;
        this.result = null;
    }

    async onSubmit() {
        this.state = 'submitting';

        const validRows = this.rows.filter(r => r._errors.length === 0);
        const request: { PurchaseOrders: BulkImportItem[] } = {
            PurchaseOrders: validRows.map(r => ({
                PurchaseOrderNumber: r.PurchaseOrderNumber,
                RequestNumber: r.RequestNumber,
                SubmitDate: r.SubmitDate,
                FabricatorID: r.FabricatorID
            }))
        };

        try {
            this.result = await this.poService.bulkImport(request).toPromise() || null;
            this.state = 'done';
        } catch (err) {
            this.state = 'previewing';
            const httpErr = err as any;
            const msg = httpErr && httpErr.error && typeof httpErr.error === 'string'
                ? httpErr.error
                : 'An unexpected error occurred during import.';
            this.toaster.showError('Import failed', msg);
        }
    }

    onCloseSuccess() {
        // pass true so the list page knows to reload
        this.dialogRef.close(true);
    }

    onCancel() {
        this.dialogRef.close(false);
    }

    downloadTemplate() {
        this.poService.downloadBulkImportTemplate().toPromise()
            .then(blob => {
                if (!blob) { return; }
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'po-bulk-import-template.xlsx';
                a.click();
                URL.revokeObjectURL(url);
            })
            .catch(() => {
                this.toaster.showError('Download failed', 'Could not download the import template.');
            });
    }
}
